import { toQueryString } from '@tuic/lib/src/helpers/to-query-string';
import { base64Encode } from '@tuic/lib/helpers/string';
import { Locale, urlWithLocale } from '@tuic/i18n';
import { HOST_APP_ACCOUNT } from '@tuic/lib/urls';

export type SignUpUrlOptions = {
  app?: string;
  intended?: string;
  email?: string;
  bookingCode?: string;
  firstName?: string;
  lastName?: string;
};

export const urls = {
  entry: (
    options?: {
      app?: string;
      intended?: string;
      email?: string;
      appSchemaRedirect?: boolean | string;
    },
    locale?: Locale,
  ) => {
    if (typeof options?.email !== 'undefined' && options.email.length > 0) {
      return urls.signIn(options);
    }

    let appSchemaRedirect = options?.appSchemaRedirect;

    if (typeof appSchemaRedirect === 'string') {
      appSchemaRedirect = appSchemaRedirect === '1';
    }

    let params = toQueryString({
      ...options,
      appSchemaRedirect: appSchemaRedirect === false ? '0' : undefined,
    });

    return urlWithLocale(locale, `/auth?${params}`);
  },

  entryAbsolute: (
    options?: {
      app?: string;
      intended?: string;
      email?: string;
      appSchemaRedirect?: boolean | string;
    },
    locale?: Locale,
  ) => {
    return HOST_APP_ACCOUNT + urls.entry(options, locale);
  },

  /**
   * Returns the relative URL of the sign in page.
   * Options:
   * - `app`: Supply the ID of the app to which the user should be redirected after login
   * - `intended`: Supply the URL to which the user should be redirected after login
   * - `email`: Supply an email address to prefill the email address field
   * - `confirmation`: Supply to display a sign up confirmation message.
   */
  signIn: (
    options?: {
      app?: string;
      intended?: string;
      email?: string;
      confirmation?: number;
      showSignUp?: boolean | string;
      appSchemaRedirect?: boolean | string;
    },
    locale?: Locale,
  ) => {
    let appSchemaRedirect = options?.appSchemaRedirect;

    if (typeof appSchemaRedirect === 'string') {
      appSchemaRedirect = appSchemaRedirect === '1';
    }

    const params = toQueryString({
      ...options,
      email: options?.email ? base64Encode(options.email) : undefined,
      appSchemaRedirect: appSchemaRedirect === false ? '0' : undefined,
    });

    return urlWithLocale(locale, `/auth/sign-in?${params}`);
  },

  /**
   * Returns the absolute URL of the sign in page.
   * Options:
   * - `app`: Supply the ID of the app to which the user should be redirected after login
   * - `intended`: Supply the URL to which the user should be redirected after login   * - `email`: Supply an email address to prefill the email address field
   * - `confirmation`: Supply to display a sign up confirmation message.
   */
  signInAbsolute: (
    options?: {
      app?: string;
      intended?: string;
      email?: string;
      confirmation?: number;
      appSchemaRedirect?: boolean | string;
    },
    locale?: Locale,
  ) => {
    return HOST_APP_ACCOUNT + urls.signIn(options, locale);
  },

  /**
   * Returns the relative URL of the sign up page.
   */
  signUp: (options?: SignUpUrlOptions, locale?: Locale) => {
    let params = toQueryString({
      ...options,
      email: options?.email ? base64Encode(options.email) : undefined,
      firstName: options?.firstName
        ? base64Encode(options.firstName)
        : undefined,
      lastName: options?.lastName ? base64Encode(options.lastName) : undefined,
    });

    return urlWithLocale(locale, `/auth/sign-up?${params}`);
  },

  /**
   * Returns the absolute URL of the sign up page.
   */
  signUpAbsolute: (options?: SignUpUrlOptions, locale?: Locale) => {
    return HOST_APP_ACCOUNT + urls.signUp(options, locale);
  },

  passwordChange: (
    options?: {
      app?: string;
      intended?: string;
    },
    locale?: Locale,
  ) => {
    return urlWithLocale(
      locale,
      `/auth/password?${toQueryString(options ?? {})}`,
    );
  },

  passwordChangeAbsolute: (
    options?: {
      app?: string;
      intended?: string;
    },
    locale?: Locale,
  ) => {
    return HOST_APP_ACCOUNT + urls.passwordChange(options, locale);
  },
};
